import CapacityElectricityDTO from "features/contracts/dtos/CapacityElectricityDTO";
import IReservedCapacityFormService from "features/requirements/services/IReservedCapacityFormService";
import { FormFields, FormValidations, SetValidationErrorFunction, SetValidationRuleFunction} from "features/shared/forms/hooks/useForm";
import PatternValidatonRule from "features/validation/rules/PatternValidatonRule";

export const reservedCapacityElectricityInitValidations = (): FormValidations => {
  return {
    DESC_TMZMSMLEESMLOUVA: {
      required: true,
      type: "OnlyRequired"
    }
  };
}

export const getCapacityValidationRule = (isRequired: boolean = true): PatternValidatonRule => {
  return {
    required: isRequired,
    type: "Pattern",
    regex: isRequired ? /^(\d+(?:[\.\,]\d{1,3})?)$/ : /^(\d+(?:[\.\,]\d{1,3})?)$|^$/,
    message: "invalidThreeDecimalsNumber"
  }
}

export const setMonthlyCapacitiesValidation = (capacities: CapacityElectricityDTO[], setValidationRule: SetValidationRuleFunction) => {
  // monthly capacities validation
  for (let i = 1; i <= 12; i++) {
    let monthCapacity = capacities.find(x => x.month == i);
    let month = i < 10 ? `0${i}` : String(i); 

    if (monthCapacity) {
      setValidationRule(`DESC_TMZMSMLEEKAP${month}`, getCapacityValidationRule());
    } else {
      setValidationRule(`DESC_TMZMSMLEEKAP${month}`, null, "remove");
    }
  }
}

export const validateMonthlyCapacity = (monthlyCapacity: string, capacity: CapacityElectricityDTO, yearlyCapacity: string): boolean => {
  let yearCapacity = yearlyCapacity ? Number(yearlyCapacity.replace(",", ".")) : capacity.yearlyCapacity!;
  let totalCapacity = Number(monthlyCapacity.replace(",", ".")) + yearCapacity;

  return totalCapacity > capacity.reserverPower! ? false : true;
}

export const validateMonthlyCapacityInput = (
  inputName: string,
  monthlyCapacity: string,
  capacity: CapacityElectricityDTO,
  yearlyCapacity: string,
  error: string,
  validateFormField: (key: string, value: any) => boolean,
  setValidationError: SetValidationErrorFunction
): boolean => {

  let isValid = false;

  if (validateFormField(inputName, monthlyCapacity)) {
    if (!validateMonthlyCapacity(monthlyCapacity, capacity, yearlyCapacity)) {
      setValidationError(inputName, error);
    } else {
      setValidationError(inputName, null, "remove");
      isValid = true;
    }  
  }

  return isValid;
}

export const validateAllCapacities = (
  formValues: Record<string, any>,
  capacities: CapacityElectricityDTO[],
  error: string,
  validateFormField: (key: string, value: any) => boolean,
  setValidationError: SetValidationErrorFunction,
  silentValidation: boolean = false
): boolean => {
  let selectedMonth: number = Number(formValues["Month"]);
  let yearlyCapacity: string = formValues["DESC_TMZMSMLEEKAPROCNI"];

  let isValid = true;
  let validationCounter = 0;

  if (validateFormField("DESC_TMZMSMLEEKAPROCNI", yearlyCapacity)) {
    for (const capacity of capacities) {
      if (capacity.month! >= selectedMonth) {
        let month = capacity.month! < 10 ? `0${capacity.month!}` : String(capacity.month!);
        let capacityInputName = `DESC_TMZMSMLEEKAP${month}`;
        let isValidInput = false;

        if (silentValidation) {
          isValidInput = validateMonthlyCapacity(String(capacity.monthlyCapacity), capacity, yearlyCapacity);
        } else {
          isValidInput = validateMonthlyCapacityInput(
            capacityInputName,
            formValues[capacityInputName],
            capacity,
            yearlyCapacity,
            error,
            validateFormField,
            setValidationError
          );
        }
        

        isValid = isValid && isValidInput;
        validationCounter++;
      }
    }
  } else {
    isValid = false;
  }

  // zero = empty capaticies (used for entire form validation)
  return validationCounter > 0 ? isValid : false;
}

export const setYearlyCapacityValidation = (capacities: CapacityElectricityDTO[], setValidationRule: SetValidationRuleFunction) => {
  if (capacities.length > 0) {
    setValidationRule(`DESC_TMZMSMLEEKAPROCNI`, getCapacityValidationRule(false));
  } else {
    setValidationRule(`DESC_TMZMSMLEEKAPROCNI`, null, "remove");
  }
}

export const validateReduceOfYearlyCapacity = async (
  formValues: FormFields,
  actualYearCapacities: CapacityElectricityDTO[],
  service: IReservedCapacityFormService
): Promise<boolean> => {

  // capacity not set
  if (formValues["DESC_TMZMSMLEEKAPROCNI"] === "") {
    return true;
  }

  let month = Number(formValues["Month"]);
  let yearlyCapacity = Number(String(formValues["DESC_TMZMSMLEEKAPROCNI"]).replace(",", "."));
  let yearlyCapOfSelMonth = actualYearCapacities.find(x => x.month === month)!.yearlyCapacity!; // in this place capacity must be searched
  
  // yearly capacity was preset to default value or is greater
  if (yearlyCapacity >= yearlyCapOfSelMonth) {
    return true;
  }

  
  let isReduced = await isReducedYearCapacityInLastYear(formValues, yearlyCapOfSelMonth, actualYearCapacities, service);
  // if yearly capacity was reduced => return false (is invalid)
  return !isReduced;
};

const isReducedYearCapacityInLastYear = async (
  formValues: FormFields,
  yearlyCapOfSelMonth: number,
  actualYearCapacities: CapacityElectricityDTO[],
  service: IReservedCapacityFormService
): Promise<boolean> => {
  let month = Number(formValues["Month"]);
  let year = Number(formValues["Year"]);
  
  let reducedCapacitiesOfActulYear = actualYearCapacities.filter((x) => x.month! < month && x.yearlyCapacity! < yearlyCapOfSelMonth);
  let reducedCapacitiesOfPrevYear = [];

  if (reducedCapacitiesOfActulYear.length == 0) {
    let previousYearCapacities = await service.fetchElectricityCapacities(formValues["DESC_TMZMSMLEESMLOUVA"], year - 1);
    reducedCapacitiesOfPrevYear = previousYearCapacities.filter((x) => x.month! >= month && x.yearlyCapacity! < yearlyCapOfSelMonth);
  }

  return reducedCapacitiesOfActulYear.length > 0 || reducedCapacitiesOfPrevYear.length > 0;
};